import type { types } from "../api";

/** Strip the variant suffix from a canonical tag name to get the version part.
 *  "3.14.2" (default) -> "3.14.2", "10.0.2-builder" -> "10.0.2". */
function versionPart(canonical: string, variant: string): string {
  if (variant === "default") return canonical;
  const suffix = `-${variant}`;
  return canonical.endsWith(suffix) ? canonical.slice(0, -suffix.length) : canonical;
}

/** Compare version strings numerically: "10.0.2" > "9.0.12" > "8.0.23". */
function compareVersions(a: string, b: string): number {
  const pa = a.split(".").map(Number);
  const pb = b.split(".").map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const diff = (pb[i] ?? 0) - (pa[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

/** Get unique canonicals from a tags list, sorted by variant (default first) then version descending. */
export function uniqueCanonicals(tags: types.Tag[]): string[] {
  const info = new Map<string, types.Tag>();
  for (const tag of tags) {
    if (!info.has(tag.canonical)) info.set(tag.canonical, tag);
  }
  return [...info.entries()].sort(([, a], [, b]) => {
    if (a.variant !== b.variant) {
      if (a.variant === "default") return -1;
      if (b.variant === "default") return 1;
      return a.variant.localeCompare(b.variant);
    }
    return compareVersions(versionPart(a.canonical, a.variant), versionPart(b.canonical, b.variant));
  }).map(([canonical]) => canonical);
}

/** Get unique streams from a tags list, sorted: "latest" first then numerically descending. */
export function uniqueStreams(tags: types.Tag[]): string[] {
  const seen = new Set<string>();
  for (const tag of tags) seen.add(tag.stream);
  return [...seen].sort((a, b) => {
    if (a === b) return 0;
    if (a === "latest") return -1;
    if (b === "latest") return 1;
    return compareVersions(a, b);
  });
}

/** Format a canonical showing all aliased tag names: "default — latest, 3.14.2, 3.14, 3". */
export function displayCanonicalWithTags(canonical: string, tags: types.Tag[]): string {
  const matching = tags.filter((t) => t.canonical === canonical);
  const variant = matching[0]?.variant ?? "";
  const names = matching.map((t) => t.name);
  if (names.length) return `${variant} — ${names.join(", ")}`;
  return canonical;
}

/** Look up the variant for a canonical from the tags list. */
export function variantOf(canonical: string, tags: types.Tag[]): string {
  return tags.find((t) => t.canonical === canonical)?.variant ?? "default";
}

/** Look up the stream for a canonical from the tags list. */
export function streamOf(canonical: string, tags: types.Tag[]): string {
  return tags.find((t) => t.canonical === canonical)?.stream ?? "latest";
}
